import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import CustomStaticPeriodDatePicker from 'Containers/Common/CustomStaticPeriodDatePicker';
import { ReactComponent as PoltronaIcon } from 'assets/svg/icons/poltrona.svg';
import { ReactComponent as CalendarioIcon } from 'assets/svg/icons/calendario.svg';
import { COMPARE_OPTIONS } from './mockData';
import styles from './StatisticNew.module.scss';

export const getPresetRange = (presetKey) => {
  const today = dayjs().startOf('day');
  switch (presetKey) {
    case 'today':
      return { start: today, end: today };
    case 'last_week':
      return { start: today.subtract(6, 'day'), end: today };
    case 'last_month':
      return { start: today.subtract(1, 'month'), end: today };
    case 'last_quarter':
      return { start: today.subtract(3, 'month'), end: today };
    case 'ytd':
      return { start: today.startOf('year'), end: today };
    default:
      return { start: null, end: null };
  }
};

const PERIOD_PRESET_OPTIONS = [
  { key: 'today', label: 'Oggi' },
  { key: 'last_week', label: 'Ultima settimana' },
  { key: 'last_month', label: 'Ultimo mese' },
  { key: 'last_quarter', label: 'Ultimo trimestre' },
  { key: 'ytd', label: 'Da inizio anno' },
  { key: 'custom', label: 'Personalizzato' },
];

export const getPresetLabel = (presetKey) =>
  PERIOD_PRESET_OPTIONS.find((item) => item.key === presetKey)?.label || 'Oggi';

export const formatPeriodLabel = (period) => {
  const { start, end } = period || {};
  if (start && end) {
    return `${start.format('DD/MM/YY')} – ${end.format('DD/MM/YY')}`;
  }
  if (start) return `Dal ${start.format('DD/MM/YY')}`;
  if (end) return `Al ${end.format('DD/MM/YY')}`;
  return 'Seleziona periodo';
};

const formatDraftDate = (date) => (date?.isValid?.() ? date.format('DD/MM/YY') : '—');

const scheduleAfterPaint = (callback) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};

const Dropdown = memo(
  ({ label, icon, options, value, onChange, open, onToggle, onClose, className, loading = false }) => {
    const wrapRef = useRef(null);
    const getOptionValue = (option) => (typeof option === 'string' ? option : option.value);
    const getOptionLabel = (option) => (typeof option === 'string' ? option : option.label);

    useEffect(() => {
      if (!open) return undefined;
      const onDocClick = (event) => {
        if (!wrapRef.current?.contains(event.target)) onClose();
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [open, onClose]);

    return (
      <div className={clsx(styles.dropdownWrap, className)} ref={wrapRef}>
        <button
          type="button"
          className={clsx(styles.ddBtn, { [styles.active]: open, [styles.ddBtnLoading]: loading })}
          onClick={onToggle}
          aria-busy={loading || undefined}
          aria-expanded={open}
        >
          {icon ? <span className={styles.ddIcon}>{icon}</span> : null}
          <span className={styles.ddLabel}>{label}</span>
          {loading ? (
            <span className={styles.ddMiniSpinner} aria-hidden />
          ) : (
            <span className={styles.chev}>▾</span>
          )}
        </button>
        {open ? (
          <div className={clsx(styles.ddMenu, styles.open)}>
            {(options || []).map((option) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              return (
                <button
                  key={optionValue}
                  type="button"
                  className={clsx({ [styles.selected]: optionValue === value })}
                  onClick={() => {
                    onChange(optionValue);
                    onClose();
                  }}
                >
                  {optionLabel}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';

const PeriodDropdown = memo(
  ({ label, icon, periodPreset, onChange, open, onToggle, onClose, loading = false }) => {
    const wrapRef = useRef(null);
    const [view, setView] = useState('list');
    const [draft, setDraft] = useState({ start: null, end: null });

    useEffect(() => {
      if (open) {
        setView('list');
        setDraft({ start: null, end: null });
      }
    }, [open]);

    useEffect(() => {
      if (!open) return undefined;
      const onDocClick = (event) => {
        if (!wrapRef.current?.contains(event.target)) onClose();
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [open, onClose]);

    const handlePresetSelect = (presetKey) => {
      if (presetKey === 'custom') {
        setView('custom');
        setDraft({ start: null, end: null });
        return;
      }
      onChange({ period: getPresetRange(presetKey), preset: presetKey });
    };

    const canApplyCustom = Boolean(draft?.start?.isValid?.() && draft?.end?.isValid?.());

    const handleApplyCustom = () => {
      if (!canApplyCustom) return;
      onChange({
        period: {
          start: draft.start.startOf('day'),
          end: draft.end.startOf('day'),
        },
        preset: 'custom',
      });
    };

    return (
      <div className={clsx(styles.dropdownWrap, styles.periodDropdown)} ref={wrapRef}>
        <button
          type="button"
          className={clsx(styles.ddBtn, { [styles.active]: open, [styles.ddBtnLoading]: loading })}
          onClick={onToggle}
          aria-busy={loading || undefined}
          aria-expanded={open}
        >
          {icon ? <span className={styles.ddIcon}>{icon}</span> : null}
          <span className={styles.ddLabel}>{label}</span>
          {loading ? (
            <span className={styles.ddMiniSpinner} aria-hidden />
          ) : (
            <span className={styles.chev}>▾</span>
          )}
        </button>
        {open ? (
          <div
            className={clsx(styles.ddMenu, styles.open, {
              [styles.periodListMenu]: view === 'list',
              [styles.periodMenu]: view === 'custom',
            })}
          >
            {view === 'list' ? (
              PERIOD_PRESET_OPTIONS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={clsx({ [styles.selected]: item.key === periodPreset })}
                  onClick={() => handlePresetSelect(item.key)}
                >
                  {item.label}
                </button>
              ))
            ) : (
              <>
                <button type="button" className={styles.periodBackBtn} onClick={() => setView('list')}>
                  ← Indietro
                </button>
                <div className={styles.periodCustomDates}>
                  <span>
                    Dal <strong>{formatDraftDate(draft.start)}</strong>
                  </span>
                  <span>
                    Al <strong>{formatDraftDate(draft.end)}</strong>
                  </span>
                </div>
                <CustomStaticPeriodDatePicker
                  period={draft}
                  setPeriod={setDraft}
                  disablePast={false}
                  disableFuture
                  variant="stats"
                />
                <button
                  type="button"
                  className={clsx(styles.periodFilterBtn, { [styles.disabled]: !canApplyCustom })}
                  disabled={!canApplyCustom}
                  onClick={handleApplyCustom}
                >
                  Filtra
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

PeriodDropdown.displayName = 'PeriodDropdown';

const StatsFiltersBar = memo(
  ({
    poltrona,
    poltronaOptions,
    poltronaLabel,
    onPoltronaChange,
    periodPreset,
    periodLabel,
    onPeriodChange,
    compare,
    onCompareChange,
    compareRefreshing = false,
  }) => {
    const [openMenu, setOpenMenu] = useState(null);
    const [periodDisplayPreset, setPeriodDisplayPreset] = useState(periodPreset);
    const [periodDisplayLabel, setPeriodDisplayLabel] = useState(periodLabel);

    useEffect(() => {
      setPeriodDisplayPreset(periodPreset);
      setPeriodDisplayLabel(periodLabel);
    }, [periodPreset, periodLabel]);

    const closeOpenMenu = useCallback(() => setOpenMenu(null), []);
    const togglePoltronaMenu = useCallback(
      () => setOpenMenu((menu) => (menu === 'poltrona' ? null : 'poltrona')),
      [],
    );
    const togglePeriodMenu = useCallback(
      () => setOpenMenu((menu) => (menu === 'period' ? null : 'period')),
      [],
    );
    const toggleCompareMenu = useCallback(
      () => setOpenMenu((menu) => (menu === 'compare' ? null : 'compare')),
      [],
    );

    const emitPeriodChange = useCallback(
      (change) => {
        const { period, preset } = change;
        setPeriodDisplayPreset(preset);
        setPeriodDisplayLabel(
          preset === 'custom' ? formatPeriodLabel(period) : getPresetLabel(preset),
        );
        flushSync(() => setOpenMenu(null));
        scheduleAfterPaint(() => onPeriodChange(change));
      },
      [onPeriodChange],
    );

    return (
      <div className={styles.filtersSticky}>
        <div className={styles.pageControls}>
          <Dropdown
            icon={<PoltronaIcon className={styles.ddIconSvg} />}
            label={poltronaLabel}
            options={poltronaOptions}
            value={poltrona}
            open={openMenu === 'poltrona'}
            onToggle={togglePoltronaMenu}
            onClose={closeOpenMenu}
            onChange={onPoltronaChange}
          />
          <PeriodDropdown
            icon={<CalendarioIcon className={styles.ddIconSvg} />}
            label={periodDisplayLabel}
            periodPreset={periodDisplayPreset}
            open={openMenu === 'period'}
            onToggle={togglePeriodMenu}
            onClose={closeOpenMenu}
            onChange={emitPeriodChange}
          />
          <Dropdown
            icon="⇄"
            label={compare}
            options={COMPARE_OPTIONS}
            value={compare}
            open={openMenu === 'compare'}
            onToggle={toggleCompareMenu}
            onClose={closeOpenMenu}
            onChange={onCompareChange}
            className={styles.compareDropdown}
            loading={compareRefreshing}
          />
        </div>
      </div>
    );
  },
);

StatsFiltersBar.displayName = 'StatsFiltersBar';

export default StatsFiltersBar;
